/**
 * ArrangeValueConvergencePanel
 * 
 * Displays value completion status for items in the ARRANGE phase.
 * Shows which items have complete values vs missing values.
 * 
 * Per Doc 00 Build Contract:
 * - Value acceptance is a persisted state transition
 * - Missing values are surfaced explicitly
 * - Links to value capture UI
 */

import { useState, useMemo, useCallback } from 'react';
import { FileCheck, AlertCircle, CheckCircle, Circle, FileText, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { UI_LABELS } from '../../../lib/terminology';
import { useValueProposalStore } from '../../../stores/prioritization/valueProposalStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useSessionReportStore } from '../../../stores/prioritization/sessionReportStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { itemInContextHref } from '@/lib/itemCycleStanding';

interface ArrangeValueConvergencePanelProps {
  cycleId: string;
}

interface ItemValueStatus {
  itemId: string;
  itemTitle: string;
  totalAttributes: number;
  acceptedCount: number;
  missingCount: number;
  pendingCount: number;
  isComplete: boolean;
}

export function ArrangeValueConvergencePanel({ cycleId }: ArrangeValueConvergencePanelProps) {
  const userId = useUserStore(state => state.userId) || 'anonymous';

  // Stores
  const attributes = useAttributesStore(state => state.attributes);
  const getCycle = useDegapProcessStore(state => state.getCycle);
  const getItemById = useItemsStore(state => state.getItemById);
  const acceptedValues = useValueProposalStore(state => state.acceptedValues);
  const getReconciliationStatus = useValueProposalStore(state => state.getReconciliationStatus);
  const createValueConvergenceReport = useSessionReportStore(state => state.createValueConvergenceReport);

  const [isCreatingReport, setIsCreatingReport] = useState(false);

  // Get cycle and its items
  const cycle = getCycle(cycleId);
  const cycleItemIds = useMemo(() => cycle?.itemIds || [], [cycle?.itemIds]);

  // Required attributes (for value completeness check)
  const requiredAttributeKeys = useMemo(() => {
    return attributes.filter(a => a.required).map(a => a.key);
  }, [attributes]);

  // Calculate value status for each item
  const itemStatuses: ItemValueStatus[] = useMemo(() => {
    return cycleItemIds.map(itemId => {
      const item = getItemById(itemId);
      const status = getReconciliationStatus(itemId, requiredAttributeKeys);
      
      return {
        itemId,
        itemTitle: item?.title || 'Unknown Item',
        totalAttributes: requiredAttributeKeys.length,
        acceptedCount: status.accepted.length,
        missingCount: status.missing.length,
        pendingCount: status.pending.length,
        isComplete: status.isReconciled,
      };
    });
  }, [cycleItemIds, getItemById, getReconciliationStatus, requiredAttributeKeys]);

  // Summary stats
  const summary = useMemo(() => {
    const total = itemStatuses.length;
    const complete = itemStatuses.filter(s => s.isComplete).length;
    const incomplete = total - complete;
    const totalMissing = itemStatuses.reduce((sum, s) => sum + s.missingCount, 0);
    const totalPending = itemStatuses.reduce((sum, s) => sum + s.pendingCount, 0);
    
    return { total, complete, incomplete, totalMissing, totalPending };
  }, [itemStatuses]);

  // Get accepted values for this cycle's items
  const cycleAcceptedValues = useMemo(() => {
    return acceptedValues.filter(av => cycleItemIds.includes(av.itemId));
  }, [acceptedValues, cycleItemIds]);

  // Create value convergence report
  const handleCreateReport = useCallback(async () => {
    if (isCreatingReport) return;
    
    setIsCreatingReport(true);
    try {
      const result = await createValueConvergenceReport({
        cycleId,
        itemIds: cycleItemIds,
        acceptedValues: cycleAcceptedValues.map(av => ({
          itemId: av.itemId,
          attributeKey: av.attributeKey,
          value: av.value,
        })),
        createdBy: userId,
      });
      
      if (result) {
        console.log('[ArrangeValueConvergencePanel] Created value convergence report:', result.id);
      }
    } finally {
      setIsCreatingReport(false);
    }
  }, [cycleId, cycleItemIds, cycleAcceptedValues, userId, createValueConvergenceReport, isCreatingReport]);

  const allComplete = summary.incomplete === 0;

  return (
    <div className={`bg-white border-2 rounded-lg p-4 ${
      allComplete ? 'border-green-200 bg-green-50' : 'border-amber-200'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <FileCheck className={`h-5 w-5 ${allComplete ? 'text-green-600' : 'text-amber-600'}`} />
          <div>
            <h3 className="font-semibold text-gray-900">Value Convergence Status</h3>
            <p className="text-sm text-gray-500">
              {summary.complete} of {summary.total} items have complete values
            </p>
          </div>
        </div>

        {/* Create Report Button */}
        {allComplete && (
          <button
            onClick={handleCreateReport}
            disabled={isCreatingReport}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 disabled:opacity-50"
          >
            <FileText className="h-4 w-4" />
            {isCreatingReport ? 'Creating Report...' : 'Create Report'}
          </button>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className={`p-3 rounded-lg ${allComplete ? 'bg-green-100' : 'bg-gray-100'}`}>
          <div className="text-2xl font-bold text-gray-900">{summary.complete}</div>
          <div className="text-xs text-gray-600">Complete</div>
        </div>
        <div className={`p-3 rounded-lg ${summary.incomplete > 0 ? 'bg-amber-100' : 'bg-gray-100'}`}>
          <div className="text-2xl font-bold text-gray-900">{summary.incomplete}</div>
          <div className="text-xs text-gray-600">Incomplete</div>
        </div>
        <div className={`p-3 rounded-lg ${summary.totalMissing > 0 ? 'bg-red-100' : 'bg-gray-100'}`}>
          <div className="text-2xl font-bold text-gray-900">{summary.totalMissing}</div>
          <div className="text-xs text-gray-600">Missing Values</div>
        </div>
      </div>

      {/* Warning if incomplete */}
      {summary.incomplete > 0 && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                {summary.incomplete} item{summary.incomplete > 1 ? 's' : ''} need value completion
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                Complete all required attribute values before proceeding to ranking.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Item List (collapsible) */}
      {itemStatuses.length > 0 && (
        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Items Status</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {itemStatuses.map(status => (
              <div
                key={status.itemId}
                className={`flex items-center justify-between p-2 rounded-lg ${
                  status.isComplete ? 'bg-green-50' : 'bg-amber-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  {status.isComplete ? (
                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  ) : (
                    <Circle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                  )}
                  <span className="text-sm font-medium text-gray-900 truncate max-w-xs">
                    {status.itemTitle}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">
                    {status.acceptedCount}/{status.totalAttributes} values
                  </span>
                  {!status.isComplete && (
                    <Link
                      to={itemInContextHref(status.itemId, { cycleId })}
                      className="text-xs text-primary-600 hover:text-primary-700"
                    >
                      Edit
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Link */}
      {summary.incomplete > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <Link
            to={`/prioritization/cycles/${cycleId}/gather`}
            className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
          >
            Go to Gather to complete values
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}

      {/* Help Text */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <p className="text-xs text-gray-500">
          {allComplete
            ? `All items have complete values. You can proceed to ${UI_LABELS.itemPool.toLowerCase()} selection.`
            : 'Complete all required attribute values before ranking.'}
        </p>
      </div>
    </div>
  );
}

export default ArrangeValueConvergencePanel;
